/**
 * v3.notifications — /v3/notifications/* (Wave 8 spine).
 *
 * The legacy `dispatch` / `send` / `listLog` calls (Wave 4-era,
 * NotificationLog 7-yr retention) stay so existing callers don't break.
 *
 * Wave 8 adds:
 *   - `dispatch(req)` — accepts the new `NotifDispatchRequest` shape
 *     (eventCode-based; templateCode optional, resolved from event binding)
 *   - `testSend(...)` — admin test path, bypass-key gated
 *   - `dispatches(filters)` — searchable dispatch history (audit viewer)
 *   - `inApp.list({ upn })`, `inApp.markRead(id)`, `inApp.dismiss(id)`
 *
 * Backend wires (PCBJxChangeApi/Functions/NotificationsFunction.cs) — see
 * Wave 8 design doc §7. UPN 'me' is resolved server-side from the bearer
 * token; the client passes `me` literally.
 */

import type {
  RequestOptions,
  ApiResult,
  GenericResult,
  DispatchNotificationRequest,
  DispatchNotificationResult,
  NotificationLogDto,
  NotificationLogFilters,
  NotifDispatchRequest,
  NotifDispatchResult,
  NotifDispatchHistoryFilters,
  NotifDispatchHistoryResponse,
  NotifTestSendRequest,
  NotifTestSendResult,
  InAppListResponse,
  InAppNotification,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

export interface NotificationLogListResponse {
  success: boolean;
  count: number;
  logs: ReadonlyArray<NotificationLogDto>;
  error?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// In-app sub-client — bell icon + dropdown surface the rows it returns.
// ─────────────────────────────────────────────────────────────────────────

class NotificationsInAppApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Fetch the current officer's in-app notifications. Pass `upn: 'me'` to
   * have the server resolve the UPN from the bearer token (canonical path);
   * an explicit UPN is honored too for SuperAdmin "view-as" flows.
   */
  public list(
    args: { upn: string; top?: number; unreadOnly?: boolean } = { upn: 'me' },
    opts?: RequestOptions
  ): Promise<InAppListResponse> {
    const { upn, top, unreadOnly } = args;
    return this.http.get<InAppListResponse>('/v3/notifications/in-app', {
      ...opts,
      query: {
        upn,
        ...(top !== undefined ? { top } : {}),
        ...(unreadOnly !== undefined ? { unreadOnly } : {}),
      },
    });
  }

  /** Mark a single in-app row read. Idempotent. */
  public markRead(id: string, opts?: RequestOptions): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/v3/notifications/in-app/${encodeURIComponent(id)}/mark-read`,
      { ...opts, body: {} }
    );
  }

  /** Dismiss a single in-app row. Idempotent. */
  public dismiss(id: string, opts?: RequestOptions): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/v3/notifications/in-app/${encodeURIComponent(id)}/dismiss`,
      { ...opts, body: {} }
    );
  }

  /** Mark every unread row read in one shot (used by "Mark all read"). */
  public markAllRead(opts?: RequestOptions): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      '/v3/notifications/in-app/mark-all-read',
      { ...opts, body: {} }
    );
  }
}

export class NotificationsApi {
  public readonly inApp: NotificationsInAppApi;

  public constructor(private readonly http: ApiHttpClient) {
    this.inApp = new NotificationsInAppApi(http);
  }

  // ── Dispatch ───────────────────────────────────────────────────────────

  /**
   * Wave 8 dispatch entry point. Accepts EITHER the new event-keyed
   * `NotifDispatchRequest` (preferred) OR the legacy `templateName`-keyed
   * `DispatchNotificationRequest`. The server discriminates on the
   * presence of `eventCode` vs `templateName` and routes accordingly.
   */
  public dispatch(
    req: NotifDispatchRequest,
    opts?: RequestOptions
  ): Promise<NotifDispatchResult>;
  public dispatch(
    req: DispatchNotificationRequest,
    opts?: RequestOptions
  ): Promise<DispatchNotificationResult>;
  public dispatch(
    req: NotifDispatchRequest | DispatchNotificationRequest,
    opts?: RequestOptions
  ): Promise<NotifDispatchResult | DispatchNotificationResult> {
    return this.http.post<NotifDispatchResult | DispatchNotificationResult>(
      '/v3/notifications/dispatch',
      { ...opts, body: req }
    );
  }

  /** Legacy alias — preserved for callers that pre-date the spine wave. */
  public send(
    req: DispatchNotificationRequest,
    opts?: RequestOptions
  ): Promise<DispatchNotificationResult> {
    return this.http.post<DispatchNotificationResult>('/v3/notifications/send', {
      ...opts,
      body: req,
    });
  }

  /**
   * Admin test-send (templateCode + sampleRecipient + sampleData). Bypass-
   * key gated server-side. `bypassKey` is forwarded as
   * `x-config-seed-bypass` to mirror the rest of the admin write surfaces.
   */
  public testSend(
    bypassKey: string,
    body: NotifTestSendRequest,
    opts?: RequestOptions
  ): Promise<NotifTestSendResult> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<NotifTestSendResult>(
      '/v3/notifications/test-send',
      { ...opts, headers, body }
    );
  }

  // ── Dispatch history (audit viewer) ────────────────────────────────────

  /**
   * Searchable, read-only dispatch history. Surfaces recipients-collapsed
   * rows (one per envelope). Officer-side filters live client-side; the
   * server-side filters here are the indexed ones (event/template/date).
   */
  public dispatches(
    filters: NotifDispatchHistoryFilters = {},
    opts?: RequestOptions
  ): Promise<NotifDispatchHistoryResponse> {
    // The backend (V3NotificationsFunction.Dispatches) reads ONLY the query
    // params `event` / `since` / `until` / `top`. Map the typed filter fields
    // to those exact names so the server-side filters actually apply — passing
    // {eventCode, fromUtc, ...} verbatim was a silent no-op (BACKLOG-0579
    // verification fix 2026-06-17). templateCode / recipient / isTestMode are
    // not server-filterable on collapsed dispatch envelopes.
    const query: Record<string, string | number | undefined> = {
      event: filters.eventCode || undefined,
      since: filters.fromUtc || undefined,
      until: filters.toUtc || undefined,
      top: filters.top ?? undefined,
    };
    return this.http.get<NotifDispatchHistoryResponse>(
      '/v3/notifications/dispatches',
      { ...opts, query }
    );
  }

  // ── Legacy NotificationLog read paths (kept for back-compat) ───────────

  public listLog(
    filters: NotificationLogFilters = {},
    opts?: RequestOptions
  ): Promise<NotificationLogListResponse> {
    return this.http.get<NotificationLogListResponse>('/v3/notifications/log', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public listForCustomer(
    cifId: string,
    opts: RequestOptions & { top?: number; since?: string } = {}
  ): Promise<ApiResult<ReadonlyArray<NotificationLogDto>>> {
    return this.http.get<ApiResult<ReadonlyArray<NotificationLogDto>>>(
      '/v3/notifications/inbox',
      {
        ...opts,
        query: {
          cifId,
          ...(opts.top !== undefined ? { top: opts.top } : {}),
          ...(opts.since !== undefined ? { since: opts.since } : {}),
        },
      }
    );
  }

  /**
   * Mark a NotificationLog row read (legacy path; pre-Wave 8). The new
   * in-app surface uses `inApp.markRead`.
   */
  public markRead(
    notificationId: string,
    opts?: RequestOptions
  ): Promise<GenericResult> {
    return this.http.post<GenericResult>(
      `/v3/notifications/${encodeURIComponent(notificationId)}/read`,
      { ...opts, body: {} }
    );
  }
}

/** Re-export a minimal shape used by the bell icon for prop typing. */
export type { InAppNotification };
